import * as z from 'zod/mini';
import { CONTACT_NAME_MAX_LENGTH, CONTACT_ROLE_MAX_LENGTH } from '../constants.ts';
import { idSchema, sortOrderSchema, timestampSchema } from './common.ts';
import { emailSchema, phoneSchema } from './user.ts';

// A contact is who to call about a client or a property: a name, what they
// are to the site, and how to reach them. One of a node's contacts can be
// the preferred one — the person to try first — and the app lists it at
// the top with a star. Contacts are read by anyone signed in and never
// hidden: the codes that get a technician through the door are the
// property's sealed access record (property-access.ts), not a contact.

export const CONTACT_NODE_TYPES = ['client', 'property'] as const;
export type ContactNodeType = (typeof CONTACT_NODE_TYPES)[number];
export const contactNodeTypeSchema = z.enum(CONTACT_NODE_TYPES);

const contactNameSchema = z
  .string()
  .check(z.trim(), z.minLength(1, 'Required'), z.maxLength(CONTACT_NAME_MAX_LENGTH));
const contactRoleSchema = z.string().check(z.trim(), z.maxLength(CONTACT_ROLE_MAX_LENGTH));

export const contactSchema = z.object({
  id: idSchema,
  nodeType: contactNodeTypeSchema,
  nodeId: idSchema,
  name: z.string(),
  role: z.string(),
  phone: z.string(),
  email: z.string(),
  preferred: z.boolean(),
  sortOrder: sortOrderSchema,
  createdAt: timestampSchema,
  updatedAt: timestampSchema,
});
export type Contact = z.infer<typeof contactSchema>;

// A patch is a partial of the plain fields, so an edit that leaves the
// preferred flag out does not clear it.
const contactFields = {
  name: contactNameSchema,
  role: contactRoleSchema,
  phone: phoneSchema,
  email: emailSchema,
  preferred: z.boolean(),
};

export const contactInputSchema = z.object({
  ...contactFields,
  preferred: z._default(contactFields.preferred, false),
});
export type ContactInput = z.infer<typeof contactInputSchema>;

export const contactPatchSchema = z.partial(z.object(contactFields));
export type ContactPatch = z.infer<typeof contactPatchSchema>;

export const contactEnvelopeSchema = z.object({ contact: contactSchema });

export const contactListSchema = z.object({
  /** The preferred contact first, then the rest in their order. */
  contacts: z.array(contactSchema),
});

/** Field key → the label its form shows; the history names edits by these. */
export const CONTACT_FIELD_LABELS: Readonly<Record<keyof ContactInput, string>> = {
  name: 'Name',
  role: 'Role',
  phone: 'Phone',
  email: 'Email',
  preferred: 'Preferred',
};
